import { Crown, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import PaywallModal from './PaywallModal';

interface PremiumBannerProps {
  variant?: 'hero' | 'inline' | 'results';
  onUpgrade?: () => void;
  onClose?: () => void;
}

export default function PremiumBanner({ variant = 'inline', onUpgrade, onClose }: PremiumBannerProps) {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  const handleClick = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      setShowModal(true);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  if (variant === 'hero') {
    return (
      <>
        <PaywallModal isOpen={showModal} onClose={handleModalClose} feature="save" />
        <div
          style={{
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            borderBottom: '1px solid #fbbf24',
            padding: '12px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Crown size={18} color="#92400e" />
            <span style={{ fontSize: '14px', color: '#92400e', fontWeight: 600 }}>
              Accede a vista mensual/anual, comparación de escenarios, exportación PDF/CSV y guarda simulaciones ilimitadas
            </span>
          </div>
          <button
            onClick={handleClick}
            style={{
              padding: '8px 16px',
              background: '#92400e',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = '#78350f';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = '#92400e';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            Ver Premium
            <ArrowRight size={14} />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                padding: '8px 12px',
                background: '#fff',
                color: '#92400e',
                border: '1px solid #fbbf24',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Ocultar
            </button>
          )}
        </div>
      </>
    );
  }

  if (variant === 'results') {
    return (
      <>
        <PaywallModal isOpen={showModal} onClose={handleModalClose} feature="save" />
        <div
          style={{
            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
            border: '2px solid #3b82f6',
            borderRadius: '12px',
            padding: '20px',
            marginTop: '24px',
            marginBottom: '24px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <Crown size={24} color="white" />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                Desbloquea todo el potencial de tus cálculos
              </h3>
              <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#475569', lineHeight: '1.6' }}>
                Con Premium puedes alternar entre vista mensual y anual, comparar múltiples escenarios lado a lado,
                exportar informes profesionales en PDF, guardar simulaciones ilimitadas y simular variaciones sin modificar tus datos.
              </p>
              <button
                onClick={handleClick}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                }}
              >
                Activar Premium ahora
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PaywallModal isOpen={showModal} onClose={handleModalClose} feature="save" />
      <div
        style={{
          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
          border: '1px solid #86efac',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <Crown size={20} color="#16a34a" />
          <div style={{ flex: 1, minWidth: '200px' }}>
            <span style={{ fontSize: '13px', color: '#166534', fontWeight: 600, display: 'block', marginBottom: '2px' }}>
              Función Premium
            </span>
            <span style={{ fontSize: '12px', color: '#166534' }}>
              Accede a comparaciones, exportación y simulaciones guardadas
            </span>
          </div>
          <button
            onClick={handleClick}
            style={{
              padding: '8px 16px',
              background: '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#15803d'}
            onMouseLeave={e => e.currentTarget.style.background = '#16a34a'}
          >
            Ver Premium
          </button>
        </div>
      </div>
    </>
  );
}
